import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { BookOpen, Plus, Trash2, Printer, Search, User, Wallet, ArrowDownCircle, ArrowUpCircle, Shield, ChevronLeft, Pencil } from 'lucide-react';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { openPrintWindow, buildPrintHTML } from '../utils/printUtils';
import LedgerEntryForm from '../components/LedgerEntryForm';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '-');

const MemberLedger = () => {
  const { user, isAdmin, isAdminOnly } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [heads, setHeads] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState('all'); // all | entrepreneur | staff | custom
  const [loading, setLoading] = useState(false);

  // Selected account for ledger drill-down
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);

  // Dialogs
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // set when editing an existing entry
  const [headDialogOpen, setHeadDialogOpen] = useState(false);

  const [accountForm, setAccountForm] = useState({
    member_type: 'entrepreneur',
    member_id: '',
    member_name: '',
    account_name: '',
    account_type: 'asset',
    opening_balance: 0,
    opening_balance_type: 'credit',
    remarks: '',
  });

  // Searchable picker query (entrepreneur/staff dropdowns inside Add Account dialog)
  const [memberQuery, setMemberQuery] = useState('');

  const [entryForm, setEntryForm] = useState({
    entry_type: 'income',
    head_name: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    reference_no: '',
  });
  const [newHeadName, setNewHeadName] = useState('');

  const [headForm, setHeadForm] = useState({ name: '', nature: 'both', description: '' });

  // Fetch data
  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [accResp, hdResp, entResp, stResp] = await Promise.all([
        api.get('/ledger/accounts'),
        api.get('/ledger/heads'),
        api.get('/entrepreneurs', { params: { limit: 5000 } }).catch(() => ({ data: [] })),
        api.get('/staff', { params: { limit: 5000 } }).catch(() => ({ data: [] })),
      ]);
      setAccounts(accResp.data || []);
      setHeads(hdResp.data || []);
      setEntrepreneurs(entResp.data || []);
      setStaff(stResp.data || []);
    } catch { if (!silent) toast.error('Failed to load ledger data'); }
    finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Real-time auto-refresh (silent — no spinner / toast)
  useAutoRefresh(() => fetchAll(true));

  // Account create
  const createAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/ledger/accounts', {
        ...accountForm,
        opening_balance: parseFloat(accountForm.opening_balance) || 0,
      });
      toast.success('Account created');
      setAccountDialogOpen(false);
      setAccountForm({ member_type: 'entrepreneur', member_id: '', member_name: '', account_name: '', account_type: 'asset', opening_balance: 0, opening_balance_type: 'credit', remarks: '' });
      setMemberQuery('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const deleteAccount = async (acc) => {
    if (!isAdminOnly()) return;
    if (!window.confirm(`Delete account "${acc.account_name}" for ${acc.member_name}? All ${acc.entry_count} entries will be permanently removed.`)) return;
    try {
      await api.delete(`/ledger/accounts/${acc.id}`);
      toast.success('Account deleted');
      if (selectedAccount?.id === acc.id) { setSelectedAccount(null); setLedgerData(null); }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Load ledger for selected account
  const openAccount = async (acc) => {
    setSelectedAccount(acc);
    setLedgerData(null);
    try {
      const r = await api.get(`/ledger/accounts/${acc.id}/entries`);
      setLedgerData(r.data);
    } catch { toast.error('Failed to load ledger'); }
  };

  const refreshLedger = () => { if (selectedAccount) openAccount(selectedAccount); };

  // Post entry (create OR update) — receives full payload from LedgerEntryForm
  const postEntry = async (payload) => {
    try {
      if (editingEntry) {
        await api.put(`/ledger/entries/${editingEntry.id}`, payload);
        toast.success('Entry updated');
      } else {
        await api.post(`/ledger/accounts/${selectedAccount.id}/entries`, payload);
        toast.success('Entry posted');
      }
      setEntryDialogOpen(false);
      setEditingEntry(null);
      refreshLedger();
      fetchAll(); // update balance in list too
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
      throw err;
    }
  };

  const openEditEntry = (entry) => {
    if (!isAdminOnly()) { toast.error('Only Admin can edit'); return; }
    setEditingEntry(entry);
    setEntryForm({
      entry_type: entry.entry_type,
      head_name: entry.head_name || '',
      amount: entry.debit_amount || entry.credit_amount || '',
      date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: entry.description || '',
      reference_no: entry.reference_no || '',
    });
    setNewHeadName('');
    setEntryDialogOpen(true);
  };

  const deleteEntry = async (entry) => {
    if (!isAdminOnly()) { toast.error('Only Admin can delete'); return; }
    if (!window.confirm('Delete this ledger entry? This cannot be undone.')) return;
    try {
      await api.delete(`/ledger/entries/${entry.id}`);
      toast.success('Entry deleted');
      refreshLedger();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Heads dialog
  const createHead = async (e) => {
    e.preventDefault();
    if (!headForm.name.trim()) { toast.error('Name required'); return; }
    try {
      await api.post('/ledger/heads', headForm);
      toast.success('Head added');
      setHeadForm({ name: '', nature: 'both', description: '' });
      const r = await api.get('/ledger/heads');
      setHeads(r.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteHead = async (h) => {
    if (!isAdminOnly()) return;
    if (!window.confirm(`Delete head "${h.name}"?`)) return;
    try {
      await api.delete(`/ledger/heads/${h.id}`);
      const r = await api.get('/ledger/heads');
      setHeads(r.data || []);
      toast.success('Deleted');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('is used in') && window.confirm(`${detail}\n\nForce delete anyway?`)) {
        try {
          await api.delete(`/ledger/heads/${h.id}?force=true`);
          const r = await api.get('/ledger/heads');
          setHeads(r.data || []);
          toast.success('Force deleted');
        } catch (e2) { toast.error(e2.response?.data?.detail || 'Failed'); }
      } else {
        toast.error(detail || 'Failed');
      }
    }
  };

  // Print ledger
  const printLedger = () => {
    if (!ledgerData || !selectedAccount) return;
    const rows = ledgerData.entries.map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}">
      <td>${dateIN(e.date)}</td>
      <td>${e.head_name}</td>
      <td>${e.description || '-'}</td>
      <td>${e.reference_no || '-'}</td>
      <td style="text-align:right">${e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
      <td style="text-align:right">${e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
      <td style="text-align:right;font-weight:bold">${fmt(e.running_balance)}</td>
    </tr>`).join('');

    const obRow = `<tr style="background:#f0fdf4;font-weight:bold">
      <td colspan="4">Opening Balance (${ledgerData.opening_balance_type})</td>
      <td style="text-align:right">${ledgerData.opening_balance_type === 'debit' ? fmt(ledgerData.opening_balance) : '-'}</td>
      <td style="text-align:right">${ledgerData.opening_balance_type === 'credit' ? fmt(ledgerData.opening_balance) : '-'}</td>
      <td></td>
    </tr>`;

    const body = `
      <div class="info">
        <p><b>Member:</b> ${selectedAccount.member_name} (${selectedAccount.member_type})</p>
        <p><b>Account:</b> ${selectedAccount.account_name} (${selectedAccount.account_type})</p>
        <p><b>Branch:</b> ${selectedAccount.branch_name || '-'}</p>
      </div>
      <div class="cards">
        <div class="card" style="background:#fef2f2"><small>Total Debit</small><b>${fmt(ledgerData.total_debit)}</b></div>
        <div class="card" style="background:#ecfdf5"><small>Total Credit</small><b>${fmt(ledgerData.total_credit)}</b></div>
        <div class="card" style="background:#eff6ff"><small>Closing Balance</small><b style="color:${ledgerData.closing_balance>=0?'#047857':'#dc2626'}">${fmt(Math.abs(ledgerData.closing_balance))} ${ledgerData.closing_balance>=0?'Cr':'Dr'}</b></div>
      </div>
      <table>
        <thead><tr><th>Date</th><th>Particulars (Head)</th><th>Description</th><th>Ref No.</th><th>Debit (Rs.)</th><th>Credit (Rs.)</th><th>Balance (Rs.)</th></tr></thead>
        <tbody>
          ${obRow}
          ${rows}
          <tr class="total-row">
            <td colspan="4"><b>Totals</b></td>
            <td style="text-align:right"><b>${fmt(ledgerData.total_debit)}</b></td>
            <td style="text-align:right"><b>${fmt(ledgerData.total_credit)}</b></td>
            <td style="text-align:right"><b>${fmt(ledgerData.closing_balance)}</b></td>
          </tr>
        </tbody>
      </table>
    `;
    openPrintWindow(buildPrintHTML(`Ledger — ${selectedAccount.member_name} (${selectedAccount.account_name})`, body, selectedAccount.branch_name || 'All Branches'));
  };

  const filteredAccounts = useMemo(() => {
    let list = accounts;
    if (memberTypeFilter !== 'all') list = list.filter((a) => a.member_type === memberTypeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) => (a.member_name || '').toLowerCase().includes(q) || (a.account_name || '').toLowerCase().includes(q));
    }
    return list;
  }, [accounts, search, memberTypeFilter]);

  // Summary stats over filtered accounts (used by the header banner)
  const stats = useMemo(() => {
    const s = { count: filteredAccounts.length, debit: 0, credit: 0, entries: 0 };
    filteredAccounts.forEach((a) => {
      s.debit += a.total_debit || 0;
      s.credit += a.total_credit || 0;
      s.entries += a.entry_count || 0;
    });
    s.net = s.credit - s.debit;
    return s;
  }, [filteredAccounts]);

  // RBAC: only Admin and Account roles can access
  if (user && !['Admin', 'Account'].includes(user.role)) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-500">Member Ledger is available only to Admin and Account roles.</p>
      </div>
    );
  }

  // === Account Detail View ===
  if (selectedAccount) {
    return (
      <div className="p-4 md:p-6 lg:p-8" data-testid="ledger-account-view">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedAccount(null); setLedgerData(null); }} data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Accounts
          </Button>
          <Button onClick={printLedger} variant="outline" data-testid="print-ledger-btn"><Printer className="w-4 h-4 mr-1" />Print Ledger</Button>
        </div>

        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 mb-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-700" />
                {selectedAccount.member_name}
              </h1>
              <p className="text-sm text-slate-600"><strong>Account:</strong> {selectedAccount.account_name} <span className="ml-3 text-[10px] uppercase px-2 py-0.5 rounded bg-slate-200 text-slate-700">{selectedAccount.account_type}</span></p>
              <p className="text-xs text-slate-500 mt-1">{selectedAccount.member_type} • {selectedAccount.branch_name || 'No branch'}</p>
            </div>
            <Button onClick={() => setEntryDialogOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-1" /> Post Entry
            </Button>
          </div>
        </div>

        {/* Ledger */}
        {!ledgerData ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Kpi label="Total Debit" value={fmt(ledgerData.total_debit)} color="red" icon={ArrowUpCircle} />
              <Kpi label="Total Credit" value={fmt(ledgerData.total_credit)} color="emerald" icon={ArrowDownCircle} />
              <Kpi label="Entries" value={ledgerData.entries.length} color="slate" />
              <Kpi
                label={`Closing Balance (${ledgerData.closing_balance >= 0 ? 'Cr' : 'Dr'})`}
                value={fmt(Math.abs(ledgerData.closing_balance))}
                color={ledgerData.closing_balance >= 0 ? 'emerald' : 'red'}
              />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-emerald-700 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Particulars (Head)</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Ref No.</th>
                      <th className="px-3 py-2 text-right">Debit (Rs.)</th>
                      <th className="px-3 py-2 text-right">Credit (Rs.)</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                      {isAdminOnly() && <th className="px-3 py-2"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr className="bg-emerald-50 font-semibold">
                      <td className="px-3 py-2" colSpan="4">Opening Balance ({ledgerData.opening_balance_type})</td>
                      <td className="px-3 py-2 text-right">{ledgerData.opening_balance_type === 'debit' ? fmt(ledgerData.opening_balance) : '-'}</td>
                      <td className="px-3 py-2 text-right">{ledgerData.opening_balance_type === 'credit' ? fmt(ledgerData.opening_balance) : '-'}</td>
                      <td className="px-3 py-2 text-right"></td>
                      {isAdminOnly() && <td></td>}
                    </tr>
                    {ledgerData.entries.length === 0 ? (
                      <tr><td colSpan={isAdminOnly() ? 8 : 7} className="px-3 py-6 text-center text-slate-400 italic">No entries yet. Click "Post Entry" to add.</td></tr>
                    ) : ledgerData.entries.map((e, i) => (
                      <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
                        <td className="px-3 py-2">{dateIN(e.date)}</td>
                        <td className="px-3 py-2 font-medium">{e.head_name}</td>
                        <td className="px-3 py-2 text-slate-600">{e.description || '-'}</td>
                        <td className="px-3 py-2 font-mono text-[10px]">{e.reference_no || '-'}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-medium">{e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-medium">{e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
                        <td className={`px-3 py-2 text-right font-bold ${e.running_balance >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(e.running_balance)}</td>
                        {isAdminOnly() && (
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditEntry(e)}
                                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                                title="Edit entry"
                                data-testid={`edit-ledger-entry-${e.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteEntry(e)}
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                title="Delete entry"
                                data-testid={`delete-ledger-entry-${e.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-bold">
                      <td className="px-3 py-2" colSpan="4">Totals</td>
                      <td className="px-3 py-2 text-right text-red-700">{fmt(ledgerData.total_debit)}</td>
                      <td className="px-3 py-2 text-right text-emerald-800">{fmt(ledgerData.total_credit)}</td>
                      <td className={`px-3 py-2 text-right ${ledgerData.closing_balance >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(ledgerData.closing_balance)}</td>
                      {isAdminOnly() && <td></td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Post Entry Dialog */}
        <Dialog
          open={entryDialogOpen}
          onOpenChange={(o) => {
            setEntryDialogOpen(o);
            if (!o) setEditingEntry(null);
          }}
        >
          <DialogContent className="max-w-lg" data-testid="post-ledger-entry-dialog">
            <DialogHeader><DialogTitle>{editingEntry ? 'Edit Ledger Entry' : 'Post Ledger Entry'}</DialogTitle></DialogHeader>
            <LedgerEntryForm
              editing={!!editingEntry}
              allowBill
              initial={editingEntry ? {
                entry_type: editingEntry.entry_type,
                date: editingEntry.date ? new Date(editingEntry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                amount: editingEntry.debit_amount || editingEntry.credit_amount || '',
                project_activity_id: editingEntry.project_activity_id || '',
                branch_id: editingEntry.branch_id || selectedAccount?.branch_id || '',
                expenditure_head_id: editingEntry.expenditure_head_id || '',
                reference_no: editingEntry.reference_no || '',
                description: editingEntry.description || '',
              } : { branch_id: selectedAccount?.branch_id || '' }}
              onSubmit={postEntry}
              onCancel={() => { setEntryDialogOpen(false); setEditingEntry(null); }}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // === Accounts List View ===
  const typeFilterChips = [
    { key: 'all', label: 'All Accounts', count: accounts.length },
    { key: 'entrepreneur', label: 'Entrepreneurs', count: accounts.filter((a) => a.member_type === 'entrepreneur').length },
    { key: 'staff', label: 'Staff', count: accounts.filter((a) => a.member_type === 'staff').length },
    { key: 'custom', label: 'Custom', count: accounts.filter((a) => a.member_type === 'custom').length },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="member-ledger-page">
      {/* HERO — gradient banner with title, actions, and stats strip */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-700 via-emerald-800 to-teal-900 text-white p-5 md:p-6 mb-5 shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <BookOpen className="w-7 h-7" /> Member Ledger
            </h1>
            <p className="text-sm text-emerald-100/90 mt-1 max-w-2xl">
              Post double-column (Dr / Cr) entries for entrepreneurs, staff or custom parties. Every entry links to a Project Activity, Block and Expenditure Head — same shape as the Expenditure module.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
          <Dialog open={headDialogOpen} onOpenChange={setHeadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="manage-heads-btn"><Wallet className="w-4 h-4 mr-1" />Manage Heads ({heads.length})</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Manage Payment Heads</DialogTitle></DialogHeader>
              <form onSubmit={createHead} className="flex gap-2 items-end mb-4">
                <div className="flex-1">
                  <Label className="text-xs">New Head Name</Label>
                  <Input value={headForm.name} onChange={e => setHeadForm({ ...headForm, name: e.target.value })} placeholder="e.g. CSR Donation" data-testid="new-head-name" />
                </div>
                <div>
                  <Label className="text-xs">Nature</Label>
                  <Select value={headForm.nature} onValueChange={v => setHeadForm({ ...headForm, nature: v })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="income">Income only</SelectItem>
                      <SelectItem value="expenditure">Expenditure only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="bg-emerald-700 text-white" data-testid="add-head-btn"><Plus className="w-4 h-4" /></Button>
              </form>
              <div className="border rounded divide-y">
                {heads.map(h => (
                  <div key={h.id} className="flex items-center justify-between p-2.5 text-sm hover:bg-slate-50">
                    <div>
                      <p className="font-medium">{h.name} {h.is_default && <span className="ml-2 text-[10px] bg-slate-200 px-1.5 py-0.5 rounded">Default</span>}</p>
                      <p className="text-xs text-slate-500 capitalize">{h.nature}</p>
                    </div>
                    {isAdminOnly() && <Button variant="ghost" size="sm" onClick={() => deleteHead(h)} className="h-7 w-7 p-0 text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-emerald-800 hover:bg-emerald-50 shadow-md" data-testid="create-account-btn"><Plus className="w-4 h-4 mr-1" />New Account</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Ledger Account</DialogTitle></DialogHeader>
              <form onSubmit={createAccount} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Member Type *</Label>
                    <Select value={accountForm.member_type} onValueChange={v => { setAccountForm({ ...accountForm, member_type: v, member_id: '', member_name: '' }); setMemberQuery(''); }}>
                      <SelectTrigger data-testid="member-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="custom">Custom (free text)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Account Type *</Label>
                    <Select value={accountForm.account_type} onValueChange={v => setAccountForm({ ...accountForm, account_type: v })}>
                      <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asset">Asset (Receivable)</SelectItem>
                        <SelectItem value="liability">Liability (Payable)</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {accountForm.member_type === 'entrepreneur' ? (
                  <div>
                    <Label>Select Entrepreneur *</Label>
                    <SearchablePicker
                      query={memberQuery}
                      setQuery={setMemberQuery}
                      items={entrepreneurs}
                      getId={(e) => e.id}
                      getLabel={(e) => `${e.enrollment_no || ''} - ${e.name}`}
                      getSubLabel={(e) => e.mobile || ''}
                      selectedId={accountForm.member_id}
                      onSelect={(e) => setAccountForm({ ...accountForm, member_id: e.id, member_name: e.name })}
                      placeholder="Type 3+ letters of name / enrollment / mobile..."
                      emptyText="No matching entrepreneur"
                      testId="ent-picker"
                    />
                  </div>
                ) : accountForm.member_type === 'staff' ? (
                  <div>
                    <Label>Select Staff *</Label>
                    <SearchablePicker
                      query={memberQuery}
                      setQuery={setMemberQuery}
                      items={staff}
                      getId={(s) => s.id}
                      getLabel={(s) => `${s.name}${s.designation ? ` (${s.designation})` : ''}`}
                      getSubLabel={(s) => s.staff_type || ''}
                      selectedId={accountForm.member_id}
                      onSelect={(s) => setAccountForm({ ...accountForm, member_id: s.id, member_name: s.name })}
                      placeholder="Type 3+ letters of staff name..."
                      emptyText="No matching staff"
                      testId="staff-picker"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Member Name (Custom) *</Label>
                    <Input value={accountForm.member_name} onChange={e => setAccountForm({ ...accountForm, member_name: e.target.value })} placeholder="e.g. External Consultant — Ravi" required data-testid="custom-name" />
                  </div>
                )}
                <div>
                  <Label>Account Name *</Label>
                  <Input value={accountForm.account_name} onChange={e => setAccountForm({ ...accountForm, account_name: e.target.value })} placeholder="e.g. Loan Account, Savings, Travel Advance" required data-testid="acc-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Opening Balance (Rs.)</Label>
                    <Input type="number" min="0" step="0.01" value={accountForm.opening_balance} onChange={e => setAccountForm({ ...accountForm, opening_balance: e.target.value })} data-testid="ob-amount" />
                  </div>
                  <div>
                    <Label>Opening Side</Label>
                    <Select value={accountForm.opening_balance_type} onValueChange={v => setAccountForm({ ...accountForm, opening_balance_type: v })}>
                      <SelectTrigger data-testid="ob-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">Credit (Cr) — receivable / in favor</SelectItem>
                        <SelectItem value="debit">Debit (Dr) — payable / owe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Textarea value={accountForm.remarks} onChange={e => setAccountForm({ ...accountForm, remarks: e.target.value })} rows={2} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-account-btn">Create Account</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>{/* /flex wrapper (title + actions row) */}

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-3">
            <div className="text-[11px] uppercase tracking-wide text-emerald-100/80 font-semibold">Accounts</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="stat-accounts">{stats.count}</div>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-3">
            <div className="text-[11px] uppercase tracking-wide text-emerald-100/80 font-semibold">Entries</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="stat-entries">{stats.entries}</div>
          </div>
          <div className="rounded-xl bg-red-500/15 backdrop-blur-sm border border-red-300/30 p-3">
            <div className="text-[11px] uppercase tracking-wide text-red-100 font-semibold flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" /> Total Debit</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="stat-debit">{fmt(stats.debit)}</div>
          </div>
          <div className="rounded-xl bg-emerald-400/15 backdrop-blur-sm border border-emerald-200/30 p-3">
            <div className="text-[11px] uppercase tracking-wide text-emerald-100 font-semibold flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" /> Total Credit</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="stat-credit">{fmt(stats.credit)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap">
        {/* Filter chips */}
        <div className="flex gap-2 flex-wrap">
          {typeFilterChips.map((chip) => {
            const active = memberTypeFilter === chip.key;
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setMemberTypeFilter(chip.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  active
                    ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm'
                    : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-emerald-50 hover:border-emerald-200'
                }`}
                data-testid={`filter-chip-${chip.key}`}
              >
                {chip.label}
                <span className={`ml-1.5 text-[10px] font-mono ${active ? 'text-emerald-100' : 'text-slate-500'}`}>({chip.count})</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[220px] flex items-center gap-2 border-l border-slate-200 pl-3">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search by member or account name..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 border-0 focus-visible:ring-0 shadow-none" data-testid="ledger-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>
      ) : filteredAccounts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl text-slate-500">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No ledger accounts match your filters. Click <strong>New Account</strong> to create one.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                <th className="px-4 py-2.5">Member</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Branch</th>
                <th className="px-4 py-2.5">Account</th>
                <th className="px-4 py-2.5 text-right">Entries</th>
                <th className="px-4 py-2.5 text-right">Debit (Dr)</th>
                <th className="px-4 py-2.5 text-right">Credit (Cr)</th>
                <th className="px-4 py-2.5 text-right">Balance</th>
                {isAdminOnly() && <th className="px-4 py-2.5 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAccounts.map((a) => {
                const positive = a.balance >= 0;
                return (
                  <tr
                    key={a.id}
                    onClick={() => openAccount(a)}
                    className="hover:bg-emerald-50/40 cursor-pointer transition"
                    data-testid={`account-${a.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold ${
                          a.member_type === 'entrepreneur' ? 'bg-blue-100 text-blue-700'
                          : a.member_type === 'staff' ? 'bg-purple-100 text-purple-700'
                          : 'bg-slate-100 text-slate-700'
                        }`}>
                          {(a.member_name || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-900">{a.member_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${
                        a.member_type === 'entrepreneur' ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : a.member_type === 'staff' ? 'bg-purple-50 text-purple-700 border border-purple-200'
                        : 'bg-slate-50 text-slate-700 border border-slate-200'
                      }`}>{a.member_type}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.branch_name && a.branch_name !== '-' ? a.branch_name : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-emerald-700 hover:underline">{a.account_name}</div>
                      <div className="text-[11px] text-slate-500 capitalize">{a.account_type}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700 font-mono text-xs">{a.entry_count || 0}</td>
                    <td className="px-4 py-3 text-right text-red-700 font-medium">{fmt(a.total_debit)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-medium">{fmt(a.total_credit)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-col items-end">
                        <span className={`text-sm font-bold ${positive ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(Math.abs(a.balance))}</span>
                        <span className={`text-[10px] font-semibold ${positive ? 'text-emerald-600' : 'text-red-500'}`}>{positive ? 'CR' : 'DR'}</span>
                      </div>
                    </td>
                    {isAdminOnly() && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); deleteAccount(a); }}
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            data-testid={`delete-acc-${a.id}`}
                            title="Delete account"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Searchable picker — filtered list shown inline, lets user type 3-4 letters to narrow down
const SearchablePicker = ({ query, setQuery, items, getId, getLabel, getSubLabel, selectedId, onSelect, placeholder, emptyText, testId }) => {
  const q = (query || '').trim().toLowerCase();
  const filtered = !q
    ? items.slice(0, 8)
    : items.filter(it => {
        const label = (getLabel(it) || '').toLowerCase();
        const sub = (getSubLabel ? getSubLabel(it) : '').toLowerCase();
        return label.includes(q) || sub.includes(q);
      }).slice(0, 25);
  const selectedItem = items.find(it => getId(it) === selectedId);
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="pl-8"
          data-testid={`${testId}-search`}
          autoComplete="off"
        />
      </div>
      {selectedItem && (
        <div className="flex items-center justify-between text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid={`${testId}-selected`}>
          <span><strong>Selected:</strong> {getLabel(selectedItem)}</span>
          <button type="button" onClick={() => { onSelect({}); setQuery(''); }} className="text-emerald-700 hover:text-emerald-900 underline">change</button>
        </div>
      )}
      {!selectedItem && (
        <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto bg-white">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">{q.length < 3 ? 'Type at least 3 letters to search…' : emptyText}</div>
          ) : (
            filtered.map(it => (
              <button
                key={getId(it)}
                type="button"
                onClick={() => { onSelect(it); setQuery(''); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 border-b last:border-b-0 border-slate-100"
                data-testid={`${testId}-option-${getId(it)}`}
              >
                <span className="font-medium text-slate-800">{getLabel(it)}</span>
                {getSubLabel && getSubLabel(it) && (
                  <span className="ml-2 text-slate-400">{getSubLabel(it)}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, color, icon: Icon }) => {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`rounded-lg border p-3 ${map[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      </div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
};

export default MemberLedger;
